// HAPPYCAP_RECURSOS_AZUL_FRESCO_V1
import { useEffect, useState } from "react";
import { getAsociaciones } from "../services/api";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const AsociacionesPage = () => {
  const { t } = useTranslation();
  const [originalAsociaciones, setOriginalAsociaciones] = useState([]);
  const [asociaciones, setAsociaciones] = useState([]);
  const [paises, setPaises] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [paisSeleccionado, setPaisSeleccionado] = useState("");
  const [ciudadSeleccionada, setCiudadSeleccionada] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAsociaciones = async () => {
      setLoading(true);
      try {
        const res = await getAsociaciones();
        setOriginalAsociaciones(res.data);
        setAsociaciones(res.data);
        const allPaises = [...new Set(res.data.map(a => a.pais).filter(Boolean))];
        const allCiudades = [...new Set(res.data.map(a => a.ciudad).filter(Boolean))];
        setPaises(allPaises);
        setCiudades(allCiudades);
        setErrorMsg("");
      } catch (error) {
        console.error("Error al cargar asociaciones", error);
        setErrorMsg(t("associations.error"));
      } finally {
        setLoading(false);
      }
    };
    fetchAsociaciones();
  }, []);

  useEffect(() => {
    const filtradas = originalAsociaciones.filter((a) => {
      const matchPais = paisSeleccionado ? a.pais === paisSeleccionado : true;
      const matchCiudad = ciudadSeleccionada ? a.ciudad === ciudadSeleccionada : true;
      return matchPais && matchCiudad;
    });

    setAsociaciones(filtradas);

    const ciudadesDisponibles = originalAsociaciones
      .filter((a) => (paisSeleccionado ? a.pais === paisSeleccionado : true))
      .map((a) => a.ciudad)
      .filter(Boolean);

    setCiudades([...new Set(ciudadesDisponibles)]);
  }, [paisSeleccionado, ciudadSeleccionada, originalAsociaciones]);

  const sortByTranslation = (arr) =>
    [...arr].sort((a, b) =>
      t(`countries.${a}`, a).localeCompare(t(`countries.${b}`, b), undefined, { sensitivity: "base" })
    );

  const sortStrings = (arr) =>
    [...arr].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

  return (
    <main className="max-w-5xl mx-auto p-6 mt-2 md:mt-5" aria-label={t("associations.title")}>
      <div className="flex flex-row items-center justify-between flex-wrap gap-4 mb-6">
        <h2 className="text-2xl font-bold text-[#247BA8] flex-1">
          {t("associations.title")}
        </h2>
        <button
          onClick={() => navigate("/recursos")}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#247BA8] text-white shadow-sm transition hover:bg-[#1B6388] hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#247BA8]/30 focus-visible:ring-offset-2"
          aria-label={t("resources.view_resources")}
        >
          <ArrowLeft size={20} />
        </button>
      </div>

      <fieldset className="mb-10 flex flex-wrap gap-4" aria-label={t("associations.filters")}>
        <legend className="sr-only">{t("associations.filters")}</legend>

        <div className="w-full min-w-0 sm:w-56">
          <select
            id="select-country"
            value={paisSeleccionado}
            onChange={(e) => {
              setPaisSeleccionado(e.target.value);
              setCiudadSeleccionada("");
            }}
            className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-base text-slate-700 shadow-sm outline-none transition hover:border-[#B8D8EA] focus:border-[#247BA8] focus:ring-2 focus:ring-[#247BA8]/15 sm:text-[15px]"
          >
            <option value="">{t("associations.country", "País")}</option>
            {sortByTranslation(paises).map((p) => (
              <option key={p} value={p}>
                {t(`countries.${p}`, p)}
              </option>
            ))}
          </select>
        </div>

        {ciudades.length > 0 && (
          <div className="w-full min-w-0 sm:w-56">
            <select
              id="select-city"
              value={ciudadSeleccionada}
              onChange={(e) => setCiudadSeleccionada(e.target.value)}
              className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-base text-slate-700 shadow-sm outline-none transition hover:border-[#B8D8EA] focus:border-[#247BA8] focus:ring-2 focus:ring-[#247BA8]/15 sm:text-[15px]"
            >
              <option value="">{t("associations.city", "Ciudad")}</option>
              {sortStrings(ciudades).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        )}
      </fieldset>

      {errorMsg && (
        <p className="text-red-600" role="alert" aria-live="assertive">
          {errorMsg}
        </p>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center mt-10 text-gray-600" role="status" aria-live="polite">
          <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mb-3" />
          <p className="text-sm">{t("associations.loading")}</p>
        </div>
      ) : asociaciones.length === 0 ? (
        null
      ) : (
        <section aria-label={t("associations.results")}>
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {asociaciones.map((a) => (
              <article
                key={a.id}
                className="bg-white shadow p-4 rounded-lg hover:shadow-md transition-shadow duration-200"
                aria-label={a.nombre}
              >
                <h3 className="font-semibold text-blue-700 mb-1">{a.nombre}</h3>
                <p>
                  <strong>{t("associations.country")}:</strong>{" "}
                  {t(`countries.${a.pais}`, a.pais)}
                </p>
                <p>
                  <strong>{t("associations.city")}:</strong> {a.ciudad}
                </p>
                {a.descripcion && <p className="text-sm mt-1">{a.descripcion}</p>}
                {a.enlace && (
                  <a
                    href={a.enlace}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-2 text-blue-500 hover:underline"
                  >
                    {t("associations.more_info")}
                  </a>
                )}
              </article>
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default AsociacionesPage;
